import { useEffect, useRef, useState } from 'react';
import { USD, int, isoDay, magnitude, pct, shortDate } from '../lib/format';
import { C, ink, surface, text } from '../lib/theme';
import type { Filters } from '../lib/types';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useFilters } from '../lib/useFilters';
import { Sparkline } from '../components/KpiCard';
import { IconAlert, IconCoverage, IconHourglass, IconShieldCheck, IconSum, IconTimer, IconUndo, IconX, IconTrendDown } from '../components/icons';
import { ErrorBanner, ExpandButton, InfoDot, SectionHeader, Skeleton } from '../components/primitives';
import { ChartCard, type Granularity } from '../components/charts/ChartCard';
import { CrosshairProvider } from '../components/charts/shared';
import { TendenciaChart } from '../components/charts/TendenciaChart';
import { ContractionChart } from '../components/charts/ContractionChart';
import { FunilChart } from '../components/charts/FunilChart';
import { TopMotivos } from '../components/charts/TopMotivos';
import { MotivosZone } from '../components/charts/MotivosZone';
import { Calendario } from '../components/charts/Calendario';
import { ClientSearch } from '../components/ClientSearch';
import { originFrom, ScaleModal, type Origin } from '../components/ScaleModal';

/**
 * Aba Visão Geral (PRD §4) — sem filtro de status, é a leitura executiva.
 * Índice: 01 KPIs + Funil (mesma fileira) · 02 Tendência · 03 Contraction vs
 * MRR · 04 Calendário · 05 Top motivos · 06 Busca.
 *
 * O Funil de auditoria (a rosca) subiu pra dentro da fileira de KPIs —
 * pedido explícito de dar mais destaque a ele logo no topo, junto do
 * resumo geral, em vez de aparecer só mais abaixo entre os gráficos. A
 * fileira virou 4 blocos lado a lado: hero (Perda Bruta), Funil, um card
 * vertical com 3 KPIs "primários" e uma coluna com 2 blocos de KPIs
 * "secundários" — todos esticados (`alignItems:'stretch'`) pra mesma altura
 * via `minHeight` no grid, sem sobra de espaço embaixo de nenhum.
 *
 * O Mapa de calor Zona × Categoria (heatmap) e o card de Falso Positivo
 * saíram daqui: o heatmap, nos dados reais do dia a dia, mostrava linha e
 * coluna com os mesmos rótulos (zona ≈ categoria pra maioria dos eventos),
 * virando uma matriz redundante em vez de reveladora — achado real do
 * Rodrigo com print. O card de falso positivo saiu por ser não-escopado ao
 * período (soma tudo, sempre), o que destoava do resto da página (que
 * segue o filtro ativo) e confundia mais do que ajudava; a contagem/alerta
 * de falso positivo continua acessível pelo ícone da Fila
 * (`FalsePositiveButton`, esse sim escopado ao período).
 *
 * Tendência e Contraction ficam dentro do mesmo CrosshairProvider: é o que
 * sincroniza a linha de referência entre os dois (PRD §9-D).
 */
export function VisaoGeralPage({ filters, onOpenClient }: {
  filters: Filters;
  onOpenClient: (masterId: string) => void;
}) {
  const { setFilter, setRange } = useFilters();
  const key = JSON.stringify(filters);
  const [gTrend, setGTrend] = useState<Granularity>('month');
  const [gCombo, setGCombo] = useState<Granularity>('month');
  const [funilOrigin, setFunilOrigin] = useState<Origin | null>(null);
  const [funilOpen, setFunilOpen] = useState(false);
  const [retOrigin, setRetOrigin] = useState<Origin | null>(null);
  const [retOpen, setRetOpen] = useState(false);
  // Filtro de canal pra Taxa de Perda (2026-09-14) — "Direto" é o padrão
  // porque a perda no numerador nunca inclui parceiro (a Nekt não manda esse
  // dado pro Hub); os outros dois são só pra medir/comparar.
  const [baseView, setBaseView] = useState<'direto' | 'total' | 'parceiro'>('direto');

  // Sincroniza com o mês vigente por padrão — mesma leitura inteligente do
  // MonthPicker da aba Clientes ("que nem a zona de clientes"). Só roda uma
  // vez na entrada da página: se o usuário limpar o período de propósito
  // depois, isso não deve voltar a forçar o mês atual.
  const didDefaultPeriod = useRef(false);
  useEffect(() => {
    if (didDefaultPeriod.current) return;
    didDefaultPeriod.current = true;
    if (!filters.from && !filters.to) {
      const now = new Date();
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      setRange(isoDay(first), isoDay(last));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const kpis = useAsync(() => api.visaoGeralKpis(filters), [key]);
  const spark = useAsync(() => api.sparkline(filters, 'mrr_lost', 14), [key]);
  const tendencia = useAsync(() => api.tendencia(filters, gTrend), [key, gTrend]);
  const combo = useAsync(() => api.contractionVsMrr(filters, gCombo), [key, gCombo]);
  const funil = useAsync(() => api.funil(filters), [key]);
  const motivos = useAsync(() => api.topMotivos(filters), [key]);
  const motivosDet = useAsync(() => api.motivosDetalhe(filters), [key]);
  const cal = useAsync(() => api.calendario(filters), [key]);
  const retirements = useAsync(() => api.retirements({ from: filters.from, to: filters.to }), [key]);

  const k = kpis.data;
  const sparkPoints = (spark.data ?? []).map((p) => magnitude(p.value));

  const coberturaBoa = Number(k?.cobertura_pct ?? 0) >= 70;
  const kpiSegments = [
    {
      label: 'Perda Confirmada', icon: <IconShieldCheck size={17} />,
      formula: 'SUM(mrr_lost) dos real_lost SEM correção manual e SEM motivo de regra — já exclui Anulado e VDI, é só a perda "limpa"',
      value: magnitude(k?.perda_confirmada), format: USD,
      // Não é `confirmados_count` (27, o total auditado) — esse número aqui
      // é só a fatia "Perda" (achado real: o rótulo antigo confundia o
      // Rodrigo, parecia que os 27 confirmados inteiros geravam esse valor).
      sub: `${int(k?.confirmados_count) - int(k?.anulado_count) - int(k?.vdi_count)} de ${int(k?.confirmados_count)} confirmados`,
      color: C.accent, chipBg: 'rgba(255,144,112,0.16)',
    },
    {
      label: 'Fila Pendente', icon: <IconHourglass size={17} />,
      formula: 'SUM(mrr_lost) e contagem dos eventos ainda em análise',
      value: magnitude(k?.fila_pendente_valor), format: USD,
      sub: `${int(k?.fila_pendente_count)} eventos aguardando`,
      color: C.amber, chipBg: 'rgba(245,165,36,0.16)',
    },
    {
      label: 'Cobertura da Auditoria', icon: <IconCoverage size={17} />,
      formula: '% dos eventos do filtro que já foram auditados (real_lost / total)',
      value: Number(k?.cobertura_pct ?? 0), format: pct,
      sub: 'quanto do trabalho já foi feito',
      color: coberturaBoa ? C.green : C.blueSoft,
      chipBg: coberturaBoa ? 'rgba(46,204,113,0.16)' : 'rgba(74,125,255,0.16)',
    },
    {
      // Renomeada de "Taxa de Exceção" (pedido explícito, 2026-09-10) —
      // aquele nome/número somava Anulado+VDI juntos, confuso. Agora é só
      // Anulado (o que JÁ foi corrigido) — VDI tem identidade e cor
      // própria em todo lugar (chip do Funil, tile do resumo da fila),
      // não precisa mais entrar nesse número. Mesmo ícone e cor do
      // "Anulado" no Funil/Waterfall — reconhecível como o mesmo conceito.
      label: 'Taxa de Correção', icon: <IconUndo size={17} />,
      formula: '% dos confirmados que já foram corrigidos manualmente (Anulado) — não inclui VDI, que ainda está pendente',
      value: Number(k?.taxa_correcao_pct ?? 0), format: pct,
      sub: `${int(k?.anulado_count)} anulado${int(k?.anulado_count) === 1 ? '' : 's'}, de ${int(k?.confirmados_count)}`,
      color: C.green, chipBg: 'rgba(46,204,113,0.16)',
    },
    {
      label: 'Tempo Médio de Auditoria', icon: <IconTimer size={17} />,
      formula: 'AVG(audited_at - created_at) dos eventos já auditados',
      value: Number(k?.tempo_medio_auditoria_dias ?? 0),
      format: (n: number) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} d`,
      sub: 'da entrada até a revisão',
      color: C.blueSoft, chipBg: 'rgba(74,125,255,0.16)',
    },
  ];
  const primaryKpis = kpiSegments.slice(0, 3);
  const secondaryKpis = kpiSegments.slice(3);

  return (
    <CrosshairProvider>
      <section style={{ marginBottom: 34 }}>
        <SectionHeader id="kpis" title="KPIs gerais" />
        {/* Funil de auditoria: última coluna, à direita (pedido explícito) —
            e a única com largura flexível (`1fr`) das quatro, então ela é
            quem absorve o espaço que sobra na fileira conforme a tela cresce
            (as outras três têm faixa fixa de largura, dimensionada só pro
            que o conteúdo delas precisa). O restante ("hero, 3 KPIs, 2 KPIs")
            usam `alignItems:'stretch'` + `minHeight` no grid pra virar uma
            única fileira de altura consistente, cada um preenchendo por
            flex/divisor interno em vez de sobrar espaço solto embaixo. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(250px,290px) minmax(220px,260px) minmax(190px,220px) minmax(380px,1fr)',
          gap: 16, alignItems: 'stretch', minHeight: 440,
        }}>
          {kpis.loading ? <Skeleton radius={22} /> : (
            <div style={{ ...surface, padding: '22px 24px 20px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 14 }}>
                <span style={{
                  width: 46, height: 46, borderRadius: 14, flex: 'none',
                  background: 'rgba(242,85,90,0.16)', color: C.churn,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  border: '1px solid rgba(242,85,90,0.34)',
                  boxShadow: '0 0 22px -3px rgba(242,85,90,0.65)',
                }}><IconSum size={24} /></span>
                <span style={{
                  fontSize: 12, fontWeight: 800, letterSpacing: '0.11em', textTransform: 'uppercase', color: text.title,
                }}>Perda Bruta</span>
                <InfoDot title="SUM(mrr_lost) de todos os eventos do filtro, auditados ou não" />
              </div>
              <div style={{
                fontSize: 39, fontWeight: 800, letterSpacing: '-0.035em', fontVariantNumeric: 'tabular-nums',
                color: C.churn, textShadow: '0 0 26px rgba(242,85,90,0.35)', lineHeight: 1.05,
              }}>{USD(magnitude(k?.perda_bruta))}</div>
              <div style={{ fontSize: 13, color: text.body, marginTop: 6 }}>· {int(k?.total_eventos)} eventos no filtro</div>
              {/* Perda Líquida — Bruta menos Anulado menos VDI (pedido
                  explícito, 2026-09-10): mostra logo abaixo da Bruta o que
                  sobra depois de tirar o que não é perda real, pra fechar a
                  conciliação com a Receita Perdida do Nekt sem esconder o
                  número bruto original. */}
              <div style={{
                display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 12, paddingTop: 12,
                borderTop: `1px solid ${ink(0.08)}`, flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: text.title }}>Líquida</span>
                <span style={{
                  fontSize: 19, fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums', color: C.accent,
                }}>{USD(magnitude(k?.perda_liquida))}</span>
                <InfoDot title="Perda Bruta − Anulado − VDI. É a perda que de fato conta depois de tirar o que foi corrigido a zero ou está pendurado numa regra." />
              </div>
              {/* Taxa de perda — Líquida ÷ MRR base do mês (quadro executivo,
                  2026-09-11). Base vem de `assinaturas_raw` (mesma Nekt), não
                  do Stripe. Split direto/parceiro (2026-09-14) via
                  `revops_client_partnership` — a perda no numerador já é só
                  canal direto (a Nekt nunca manda parceiro pro Hub), então o
                  padrão aqui é a base direto, não o total; o filtro deixa
                  medir o que antes só existia como aviso no InfoDot. */}
              {k?.mrr_base !== null && k?.mrr_base !== undefined && (() => {
                const bases = {
                  direto: { valor: k.mrr_base_direto ?? 0, subs: k.subs_ativas_direto ?? 0, label: 'Direto' },
                  total: { valor: k.mrr_base ?? 0, subs: k.subs_ativas ?? 0, label: 'Total' },
                  parceiro: { valor: k.mrr_base_parceiro ?? 0, subs: k.subs_ativas_parceiro ?? 0, label: 'Parceiro' },
                } as const;
                const sel = bases[baseView];
                const pct = sel.valor > 0 ? (magnitude(k.perda_liquida) / sel.valor) * 100 : 0;
                return (
                  <div style={{ marginTop: 5 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{
                        fontSize: 15, fontWeight: 800, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums', color: C.churn,
                      }}>{pct.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: text.title, letterSpacing: '0.04em', textTransform: 'uppercase' }}>da base {sel.label.toLowerCase()}</span>
                      <InfoDot title="Líquida ÷ MRR base do canal selecionado. Base = soma do MRR das assinaturas ativas no período (nekt_sync.assinaturas_raw), classificadas por cliente via revops_client_partnership. Padrão é Direto — a perda no numerador nunca inclui parceiro." />
                      <div style={{ display: 'flex', gap: 3, marginLeft: 'auto' }}>
                        {(Object.keys(bases) as Array<keyof typeof bases>).map((key) => (
                          <button
                            key={key} type="button" onClick={() => setBaseView(key)}
                            style={{
                              border: 'none', cursor: 'pointer', font: 'inherit', fontSize: 9.5, fontWeight: 700,
                              padding: '3px 7px', borderRadius: 6,
                              background: baseView === key ? ink(0.12) : 'transparent',
                              color: baseView === key ? C.fg : ink(0.4),
                            }}
                          >{bases[key].label}</button>
                        ))}
                      </div>
                    </div>
                    <div style={{ fontSize: 11.5, color: text.body, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                      {USD(sel.valor)} · {int(sel.subs)} assinaturas
                    </div>
                  </div>
                );
              })()}
              {/* Churn vs Contraction — barra empilhada com % e ticket médio
                  (quadro executivo). Sobre a Bruta, tipo efetivo. */}
              {(() => {
                const churnV = magnitude(k?.churn_valor);
                const contrV = magnitude(k?.contraction_valor);
                const churnN = int(k?.churn_count);
                const contrN = int(k?.contraction_count);
                const churnP = Number(k?.churn_pct ?? 0);
                const contrP = Number(k?.contraction_pct ?? 0);
                if (churnV + contrV === 0) return null;
                return (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${ink(0.08)}` }}>
                    <div style={{ display: 'flex', height: 7, borderRadius: 99, overflow: 'hidden', background: ink(0.08) }}>
                      <div style={{ width: `${churnP}%`, background: C.churn, transition: 'width 0.4s ease' }} />
                      <div style={{ width: `${contrP}%`, background: C.amber, transition: 'width 0.4s ease' }} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 8, fontSize: 11 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ width: 7, height: 7, borderRadius: 2, background: C.churn, flex: 'none' }} />
                          <span style={{ fontWeight: 800, color: C.churn, fontVariantNumeric: 'tabular-nums' }}>{churnP.toFixed(0)}%</span>
                          <span style={{ color: text.title, fontWeight: 700 }}>churn</span>
                        </div>
                        <div style={{ color: text.body, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                          {USD(churnV)} · {churnN} ev · {churnN > 0 ? USD(churnV / churnN) : '—'} méd
                        </div>
                      </div>
                      <div style={{ minWidth: 0, textAlign: 'right' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
                          <span style={{ color: text.title, fontWeight: 700 }}>contraction</span>
                          <span style={{ fontWeight: 800, color: C.amber, fontVariantNumeric: 'tabular-nums' }}>{contrP.toFixed(0)}%</span>
                          <span style={{ width: 7, height: 7, borderRadius: 2, background: C.amber, flex: 'none' }} />
                        </div>
                        <div style={{ color: text.body, marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                          {USD(contrV)} · {contrN} ev · {contrN > 0 ? USD(contrV / contrN) : '—'} méd
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div style={{ flex: 1, minHeight: 30, marginTop: 12, display: 'flex', alignItems: 'flex-end' }}>
                <Sparkline points={sparkPoints} color={C.churn} height={64} />
              </div>
              {/* Disclaimer de "retirado pela fonte" (2026-09-14) — só
                  aparece quando existe algo pra explicar. É constatação
                  técnica automática (natural_key que a Nekt parou de
                  reconhecer), nunca decisão de auditoria — por isso vive
                  separado de Anulado/VDI, com histórico próprio. */}
              {(retirements.data?.length ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={(e) => { setRetOrigin(originFrom(e)); setRetOpen(true); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, marginTop: 10, paddingTop: 10,
                    borderTop: `1px solid ${ink(0.08)}`, borderLeft: 'none', borderRight: 'none', borderBottom: 'none',
                    background: 'transparent', cursor: 'pointer', font: 'inherit', textAlign: 'left', width: '100%',
                  }}
                >
                  <IconAlert size={12} />
                  <span style={{ fontSize: 11, color: text.body }}>
                    {retirements.data!.length} evento{retirements.data!.length === 1 ? '' : 's'} retirado{retirements.data!.length === 1 ? '' : 's'} pela fonte, {USD(magnitude(retirements.data!.reduce((s, r) => s + magnitude(r.original_mrr_lost), 0)))} — a Nekt não reconhece mais essas chaves
                  </span>
                </button>
              )}
            </div>
          )}

          {/* Retângulo vertical com 3 KPIs (pedido explícito) — os "primários":
              valor confirmado, fila pendente e cobertura. Divisores internos
              horizontais, sem caixa própria por item — mesma receita da
              barra "Resumo da fila". */}
          <div style={{ ...surface, padding: '18px 20px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {primaryKpis.map((seg, i) => (
              <div key={seg.label} style={{
                flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                borderTop: i > 0 ? `1px solid ${ink(0.08)}` : 'none',
                paddingTop: i > 0 ? 14 : 0, marginTop: i > 0 ? 14 : 0,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: 10, flex: 'none',
                    background: seg.chipBg, color: seg.color,
                    border: `1px solid ${seg.color}38`, boxShadow: `0 0 14px -4px ${seg.color}99`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{seg.icon}</span>
                  <span style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
                    color: text.title, minWidth: 0, lineHeight: 1.3, flex: 1,
                  }}>{seg.label}</span>
                  <InfoDot title={seg.formula} />
                </div>
                {kpis.loading ? <Skeleton height={28} radius={8} /> : (
                  <>
                    <div style={{
                      fontSize: 23, fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
                      color: seg.color,
                    }}>{seg.format(seg.value)}</div>
                    <div style={{ fontSize: 11.5, color: text.body, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{seg.sub}</div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Dois blocos "secundários" (diagnóstico, não volume financeiro):
              taxa de exceção e tempo médio, cada um seu próprio card. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
            {secondaryKpis.map((seg) => (
              <div key={seg.label} style={{ ...surface, flex: 1, minHeight: 0, padding: '16px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                  <span style={{
                    width: 32, height: 32, borderRadius: 10, flex: 'none',
                    background: seg.chipBg, color: seg.color,
                    border: `1px solid ${seg.color}38`, boxShadow: `0 0 14px -4px ${seg.color}99`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>{seg.icon}</span>
                  <span style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
                    color: text.title, minWidth: 0, lineHeight: 1.3, flex: 1,
                  }}>{seg.label}</span>
                  <InfoDot title={seg.formula} />
                </div>
                {kpis.loading ? <Skeleton height={24} radius={8} /> : (
                  <>
                    <div style={{
                      fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', fontVariantNumeric: 'tabular-nums',
                      color: seg.color,
                    }}>{seg.format(seg.value)}</div>
                    <div style={{ fontSize: 11, color: text.body, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{seg.sub}</div>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Funil de auditoria — última coluna, à direita, e a única
              flexível: recebe o espaço que sobra na fileira, então cresce
              proporcionalmente (card, chips do topo e rosca, todos maiores
              que nas outras colunas — pedido explícito) em vez de ficar com
              vão vazio ao redor. Card próprio no mesmo estilo do hero (não
              `ChartCard`, que impõe altura fixa em vez de preencher a
              fileira), com botão de expandir reaproveitando o Scale Modal
              padrão de gráfico. */}
          <div style={{ ...surface, padding: '24px 28px 22px', display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flex: 'none' }}>
              <span style={{ fontSize: 18.5, fontWeight: 750, letterSpacing: '-0.012em' }}>Funil de auditoria</span>
              <span style={{ marginLeft: 'auto' }}>
                <ExpandButton onClick={(e) => { setFunilOrigin(originFrom(e)); setFunilOpen(true); }} size={32} />
              </span>
            </div>
            {funil.loading ? <Skeleton radius={14} />
              : funil.error ? <ErrorBanner message={funil.error} onRetry={funil.reload} />
              : <div style={{ flex: 1, minHeight: 0 }}><FunilChart rows={funil.data ?? []} size="lg" /></div>}
          </div>
        </div>
      </section>

      <ScaleModal
        open={funilOpen} origin={funilOrigin} onClose={() => setFunilOpen(false)}
        title="Funil de auditoria" width={640}
      >
        <div style={{ height: 440 }}><FunilChart rows={funil.data ?? []} /></div>
      </ScaleModal>

      <ScaleModal
        open={retOpen} origin={retOrigin} onClose={() => setRetOpen(false)}
        title="Retirado pela fonte" subtitle="Chaves que a Nekt não reconhece mais no feed vivo — constatação técnica, não decisão de auditoria" width={560}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(retirements.data ?? []).map((r) => {
            const isChurn = r.original_event_type === 'churn';
            return (
              <div
                key={String(r.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 11px', borderRadius: 10,
                  background: ink(0.04), border: `1px solid ${ink(0.08)}`,
                }}
              >
                <span style={{ color: isChurn ? C.churn : C.amber, display: 'flex', flex: 'none' }}>
                  {isChurn ? <IconX size={12} /> : <IconTrendDown size={13} />}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: C.fg }}>
                    {r.master_id}{r.product_category ? ` · ${r.product_category}` : ''}
                  </div>
                  <div style={{ fontSize: 11, color: text.muted, marginTop: 2 }}>
                    original {shortDate(r.original_event_date)} · detectado {shortDate(r.detected_at)}
                  </div>
                </div>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: ink(0.75), fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                  {USD(magnitude(r.original_mrr_lost))}
                </span>
              </div>
            );
          })}
          {(retirements.data ?? []).length === 0 && (
            <div style={{ fontSize: 12.5, color: text.muted, padding: '16px 4px' }}>Nada retirado neste período.</div>
          )}
        </div>
      </ScaleModal>

      <section id="tendencia" style={{ marginBottom: 26, scrollMarginTop: 150 }}>
        <ChartCard
          title="Tendência de MRR perdido"
          granularity={gTrend} onGranularity={setGTrend}
          loading={tendencia.loading} error={tendencia.error} onRetry={tendencia.reload} height={280}
        >
          <TendenciaChart
            rows={tendencia.data ?? []} granularity={gTrend}
            onBrush={(from, to) => setRange(from, to)}
          />
        </ChartCard>
      </section>

      <section id="contraction" style={{ marginBottom: 26, scrollMarginTop: 150 }}>
        <ChartCard
          title="Contração vs MRR pendente"
          granularity={gCombo} onGranularity={setGCombo}
          loading={combo.loading} error={combo.error} onRetry={combo.reload} height={280}
        >
          <ContractionChart rows={combo.data ?? []} granularity={gCombo} />
        </ChartCard>
      </section>

      {/* Calendário ficou sozinho aqui embaixo — o Funil, seu antigo par,
          subiu pra fileira de KPIs. Conteúdo compacto (grade fixa de
          pixels), então `expandedWidth` menor evita modal esticado. */}
      <section id="calendario" style={{ marginBottom: 26, scrollMarginTop: 150 }}>
        <ChartCard
          title="Calendário de perdas por dia" expandedWidth={760}
          loading={cal.loading} error={cal.error} onRetry={cal.reload} height={220}
        >
          <Calendario rows={cal.data ?? []} onPick={(day) => setRange(day, day)} />
        </ChartCard>
      </section>

      <section id="motivos" style={{ marginBottom: 26, scrollMarginTop: 150, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SectionHeader title="Motivos de perda" />
        {motivosDet.error ? <ErrorBanner message={motivosDet.error} onRetry={motivosDet.reload} />
          : !motivosDet.data ? <Skeleton height={320} />
          : <MotivosZone data={motivosDet.data} onPickReason={(id) => setFilter('reason_id', id)} />}
        <ChartCard
          title="Clientes por motivo principal"
          loading={motivos.loading} error={motivos.error} onRetry={motivos.reload} height={260}
        >
          <TopMotivos rows={motivos.data ?? []} onPick={(id) => setFilter('reason_id', id)} onOpenClient={onOpenClient} />
        </ChartCard>
      </section>

      <section id="busca" style={{ scrollMarginTop: 150 }}>
        <SectionHeader title="Busca de clientes" />
        <div style={{ ...surface, padding: 20 }}>
          <ClientSearch onOpenClient={onOpenClient} />
        </div>
      </section>
    </CrosshairProvider>
  );
}
